import { PerfilService } from "src/app/services/ajs-perfil.service";
import { formatCurrency } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatCheckboxChange,
  MatDialogRef,
  MatSnackBar,
} from "@angular/material";
import { TextFormats } from "@mandae/ng-utils/mask";
import { createNumberMask } from "text-mask-addons/dist/textMaskAddons";
import { Address } from "../../forms/address-form/address-form.component";
import { brlRangeValidator } from "../../forms/brl-range.validator";
import { cpfCnpjValidator, validateCnpj } from "../../forms/cpf-cnpj.validator";
import { cpfCnpjMask } from "../../forms/cpf-cnpj.mask";
import { ShippingService } from "../../services/api/shipping-service-api.service";

const MODELO_NFE = '55';
const MODELO_DCE = '99';
const PESOS_DV: ReadonlyArray<number> = [
  4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2,
  9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2,
];

function chaveAcessoValidator(control: AbstractControl): ValidationErrors | null {
  const value: string = (control.value || '').replace(/\D/g, '');
  if (!value) {
    return null;
  }
  if (value.length !== 44) {
    return null;
  }

  const modelo = value.substring(20, 22);
  if (modelo !== MODELO_NFE && modelo !== MODELO_DCE) {
    return { invalidModel: true };
  }

  let soma = 0;
  for (let i = 0; i < 43; i++) {
    soma += PESOS_DV[i] * parseInt(value.charAt(i), 10);
  }
  const resto = soma % 11;
  const dvCalculado = resto <= 1 ? 0 : 11 - resto;
  const dvInformado = parseInt(value.charAt(43), 10);
  if (dvCalculado !== dvInformado) {
    return { invalidCheckDigit: true };
  }

  return null;
}

@Component({
  selector: "mandae-package-create-form",
  templateUrl: "./package-create-form.component.html",
  styleUrls: ["./package-create-form.component.scss"],
  providers: [
    { provide: MatDialogRef, useValue: {} },
    { provide: MAT_DIALOG_DATA, useValue: {} },
  ],
})
export class PackageCreateFormComponent implements OnChanges {
  public ignoraValidacaoChaveAcessoNfe = false;

  public loading = false;

  public tipoDocumento = "NFe";

  @Input()
  public error: any;

  @Output()
  public complete = new EventEmitter<any>();

  public get name() {
    return this.form.get("name");
  }

  public get email() {
    return this.form.get("email");
  }

  public get document() {
    return this.form.get("document");
  }

  public get phone() {
    return this.form.get("phone");
  }

  public get NFe() {
    return this.form.get("NFe");
  }

  public get isCnpj() {
    return validateCnpj(this.form.get("document").value);
  }

  public address: Address;
  public form: FormGroup;

  private _shippingService: ShippingService;
  public get shippingService() {
    return this._shippingService;
  }
  public set shippingService(value: ShippingService) {
    console.log('Serviço de envio selecionado:', value);
    this._shippingService = value;
    this.updateInsurance();
  }

  public get insuredValueError() {
    const control = this.form.get("insuredValue");
    if (!control) {
      return null;
    }

    if (!control.hasError("range")) {
      return null;
    }

    if (control.hasError("required")) {
      return "O valor declarado é obrigatório caso deseje contratar o seguro";
    }

    const error = control.getError("range");
    if (error.value < error.min) {
      return (
        "O valor declarado deve ser maior que " +
        formatCurrency(error.min, "pt", "R$", "BRL", "1.2-2")
      );
    }

    return (
      "O valor declarado deve ser menor que " +
      formatCurrency(error.max, "pt", "R$", "BRL", "1.2-2")
    );
  }

  public get insuranceCost() {
    if (!this.shippingService) {
      return null;
    }

    if (!this.form.get("insurance").value) {
      return null;
    }

    const value = this.form.get("insuredValue");
    if (!value.value || value.hasError("range")) {
      return null;
    }

    return (
      (+value.value.replace(/[^0-9,]/g, "").replace(",", ".") *
        this.shippingService.percentualValorDeclarado) /
      100
    );
  }

  public get valid() {
    return this.shippingService && this.address && this.form.valid;
  }

  public readonly phoneMask = TextFormats.phoneMask;
  public readonly documentMask = cpfCnpjMask;
  public readonly brlMask = createNumberMask({
    prefix: "R$ ",
    thousandsSeparatorSymbol: ".",
    allowDecimal: true,
    decimalSymbol: ",",
    requireDecimal: true,
  });

  constructor(private profile: PerfilService, private snackBar: MatSnackBar) {
    const insurance = new FormControl(true);
    this.form = new FormGroup({
      name: new FormControl("", [Validators.required]),
      phone: new FormControl(""),
      email: new FormControl("", [Validators.email]),
      document: new FormControl("", [Validators.required, cpfCnpjValidator]),
      stateDocument: new FormControl(""),
      typeDocument: new FormControl("NFe"),
      NFe: new FormControl("", [
        Validators.minLength(44),
        Validators.maxLength(44),
        Validators.pattern(/^\d{44}$/),
        chaveAcessoValidator,
        (control: AbstractControl): ValidationErrors | null => {
          const chave: string = (control.value || '').replace(/\D/g, '');
          if (!chave || chave.length !== 44) {
            return null;
          }
          if (!this.ignoraValidacaoChaveAcessoNfe) {
            return null;
          }
          const modelo = chave.substring(20, 22);
          if (this.tipoDocumento === 'NFe' && modelo !== MODELO_NFE) {
            return { typeMismatch: { selected: 'NF-e', keyType: 'DC-e' } };
          }
          if (this.tipoDocumento === 'DCe' && modelo !== MODELO_DCE) {
            return { typeMismatch: { selected: 'DC-e', keyType: 'NF-e' } };
          }
          return null;
        },
      ]),
      insurance,
      insuredValue: new FormControl("", []),
      reference: new FormControl(""),
      quantidadeVolumes: new FormControl("", [
        Validators.required,
        Validators.pattern(/^[1-9]\d*$/),
        Validators.max(500)
      ]),
      observation: new FormControl(""),
    });

    insurance.valueChanges.subscribe(() => this.updateInsurance());
    this.loading = true;
    this.getUserData();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.error && changes.error.currentValue) {
      const err = changes.error.currentValue;

      if (err.data && err.data.erros) {
        const message =
          err.data.erros[Object.keys(err.data.erros).shift()] || "Erro interno";
        this.snackBar.open("Erro ao inserir a encomenda: " + message, "OK", {
          duration: 2500,
        });
      } else {
        this.snackBar.open(
          "Ocorreu um erro interno ao inserir a encomenda",
          "OK",
          {
            duration: 2500,
          }
        );
      }
    }
  }

  public async getUserData() {
    try {
      const profile = await this.profile.getUserData(true);
      this.ignoraValidacaoChaveAcessoNfe =
        profile.ignoraValidacaoChaveAcessoNfe;

      this.loading = false;
    } catch (error) {}
  }

  public handleFormSubmit() {
    if (!this.valid) {
        return;
    }

    this.inferirTipoDocumentoPelaChave();

    this.loading = true;
    setTimeout(() => {
      this.complete.emit({
        address: this.address,
        shippingService: this.shippingService,
        packageData: this.form.value,
        typeDocument: this.tipoDocumento,
      });
    }, 2000);
  }

  public handleInsuranceChange(
    ev: MatCheckboxChange,
    insuredValueInput: HTMLInputElement
  ) {
    if (ev.checked) {
      setTimeout(() => insuredValueInput.focus(), 0);
    }
  }

  public changeTypeDocument() {
    this.tipoDocumento = this.form.get("typeDocument").value;
    this.form.get("NFe").updateValueAndValidity();
  }

  private inferirTipoDocumentoPelaChave(): void {
    if (this.ignoraValidacaoChaveAcessoNfe) {
      return;
    }
    const chave: string = (this.form.get("NFe").value || '').replace(/\D/g, '');
    if (chave.length !== 44) {
      return;
    }
    const modelo = chave.substring(20, 22);
    if (modelo === MODELO_DCE) {
      this.tipoDocumento = 'DCe';
    } else {
      this.tipoDocumento = 'NFe';
    }
    this.form.get("typeDocument").setValue(this.tipoDocumento, { emitEvent: false });
  }

  private updateInsurance() {
    const insuredValue = this.form.get("insuredValue");
    const insurance = this.form.get("insurance");

    let validators = [];
    if (this.shippingService && insurance.value) {
      validators = [
        Validators.required,
        brlRangeValidator(
          this.shippingService.valorDeclaradoMinimo,
          this.shippingService.valorDeclaradoMaximo
        ),
      ];
    }

    insuredValue.setValidators(validators);

    if (!insurance.value) {
      insuredValue.setValue("");
    }

    insuredValue.updateValueAndValidity();
  }
}
